import React, { useEffect, useState } from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../components/BuildingBlocks/SpacingRow";
import { Background } from "../../components/BuildingBlocks/Background";
import { Text } from "../../components/BuildingBlocks/Text";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { FontRoboto } from "../../components/BuildingBlocks/FontRoboto";
import { TextColor } from "../../components/BuildingBlocks/TextColor";
import { CRMTitleSection } from "../../components/CRMTitleSection/CRMTitleSection";
import { CRMTitleSubSection } from "../../components/CRMTitleSubSection/CRMTitleSubSection";
import { CRMSpacer } from "../../components/CRMSpacer/CRMSpacer";
import { CRMHorizontalSeperatorLine } from "../../components/CRMHorizontalSeperatorLine/CRMHorizontalSeperatorLine";
import { CRMAutosaveIndicatorWrap } from "../../components/CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { CRMInputLabel } from "../../components/CRMInputLabel/CRMInputLabel";
import { CRMTitleParagraph } from "../../components/CRMTitleParagraph/CRMTitleParagraph";
import { CRMTitleParagraphMono } from "../../components/CRMTitleParagraphMono/CRMTitleParagraphMono";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { WeightMedium } from "../../components/WeightMedium/WeightMedium";
import { CRMStatusPill } from "../../components/Simple/CRMStatusPill/CRMStatusPill";
import { CRMButtonQuaternary } from "../../components/CRMButtonQuaternary/CRMButtonQuaternary";
import { CRMButtonIcon } from "../../components/CRMButtonIcon/CRMButtonIcon";
import { CRMLoadingBar } from "../../components/Simple/CRMLoadingBar/CRMLoadingBar";
import { CRMFlatInputGeneral } from "../../components/CRMFlatInputGeneral/CRMFlatInputGeneral";
import { CRMSpacing } from "../../models/CRMSpacing";
import { CRMColors } from "../../models/CRMColors";
import { THexColor } from "../../models/StringLiterals";
import { TCasesEpcStatus } from "../../../../domain/codecs/CasesEpc";

const statusPill = (status: TCasesEpcStatus, hasReceivedDocument: boolean): { label: string, pillColor: THexColor } => {
    if (hasReceivedDocument && status !== "valid" && status !== "not_required") {
        return { label: "Certificate on file", pillColor: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2 };
    }
    switch (status) {
        case "valid": return { label: "Valid", pillColor: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2 };
        case "not_required": return { label: "Not required", pillColor: CRMColors.HIGHLIGHTS_CALMING_PINK_2 };
        case "expired": return { label: "Expired", pillColor: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2 };
        case "none_found": return { label: "Not found", pillColor: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_2 };
        case "pending": return { label: "No automatic match", pillColor: CRMColors.NEUTRAL_4 };
    }
};

const EpcField = (props: { label: string, children: React.ReactNode }): JSX.Element => (
    <SpacingColumn spacing={CRMSpacing.TINY}>
        <CRMInputLabel>{props.label}</CRMInputLabel>
        {props.children}
    </SpacingColumn>
);

const CRMLegalCaseDetailsEpcContainer = (props: TContainerStateProps): JSX.Element => {
    const epc = props.state.forms.case_details_page.data.output.details.children.epc;
    const data = epc.data;
    const isSale = props.state.forms.case_details_page.data.output.details.children.transaction_type === "sale";

    const status: TCasesEpcStatus = data ? data.status : "pending";
    const hasReceivedDocument = epc.has_received_epc_document;
    const pill = statusPill(status, hasReceivedDocument);
    const needsNewEpc = (status === "expired" || status === "none_found") && !hasReceivedDocument;
    const needsCertificate = status !== "valid" && status !== "not_required";
    const results = epc.search.children.results;

    const isSearching = epc.search.status === "submitting";
    const isAttaching = epc.attach.status === "submitting";
    const attachingCertificateNumber = epc.attach.edited.certificate_number;

    const [searchTerm, setSearchTerm] = useState<string>(needsCertificate ? epc.property_postcode : "");

    const normalisedSearchTerm = searchTerm.trim().toLowerCase();
    const hasSearchedCurrentTerm =
        epc.search.status === "success"
        && epc.search.original.search_term.trim() !== ""
        && epc.search.original.search_term.trim().toLowerCase() === normalisedSearchTerm;

    useEffect(() => {
        if (searchTerm.trim() === "") {
            return;
        }
        const timeout = setTimeout(() => {
            props.dispatch({
                type: "LEGAL_CASE_EPC_SEARCH",
                payload: { ...epc.search, edited: { ...epc.search.edited, search_term: searchTerm } },
            });
        }, 600);
        return () => clearTimeout(timeout);
    }, [searchTerm]);

    return (
        <CRMAutosaveIndicatorWrap status={props.state.forms.case_details_page.data.output.details.status}>
            <CRMTitleSection>
                EPC
            </CRMTitleSection>
            <CRMSpacer size="large" />
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                {/* STATUS */}
                <EpcField label="Status">
                    <SpacingRow spacing={CRMSpacing.TINY}>
                        <CRMStatusPill label={pill.label} pillColor={pill.pillColor} borderColor={pill.pillColor} textColor={CRMColors.NEUTRAL_PAPER} />
                    </SpacingRow>
                </EpcField>

                {/* FOUND CERTIFICATE */}
                {data !== null && data.certificate_number !== null &&
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <EpcField label="Energy rating">
                            <CRMTitleParagraph>{data.rating ?? "Unknown"}</CRMTitleParagraph>
                        </EpcField>
                        <EpcField label="Certificate number">
                            <CRMTitleParagraphMono>{data.certificate_number}</CRMTitleParagraphMono>
                        </EpcField>
                        <EpcField label="Valid until">
                            <CRMTitleParagraph>{data.expiry_date ?? "Unknown"}</CRMTitleParagraph>
                        </EpcField>
                        {data.source_url !== null &&
                            <SpacingRow spacing={CRMSpacing.SMALL}>
                                <CRMButtonQuaternary
                                    label="View official certificate"
                                    icon="file-clip"
                                    onClick={() => window.open(data.source_url ?? "", "_blank")}
                                />
                            </SpacingRow>
                        }
                    </SpacingColumn>
                }

                {/* ARRANGING A NEW EPC (sale, no valid EPC) */}
                {isSale && needsNewEpc && data !== null &&
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <CRMHorizontalSeperatorLine />
                        <CRMTitleSubSection>Arranging a new EPC</CRMTitleSubSection>
                        <EpcField label="Who is arranging the new EPC?">
                            <CRMTitleParagraph>
                                {data.who_arranges_new_epc === "us" ? "Sail Legal"
                                    : data.who_arranges_new_epc === "client" ? "The client"
                                    : "Not yet decided"}
                            </CRMTitleParagraph>
                        </EpcField>
                        {data.who_arranges_new_epc === "us" &&
                            <SpacingRow spacing={CRMSpacing.SMALL}>
                                <CRMButtonQuaternary
                                    label={data.new_epc_sorted ? "New EPC sorted ✓" : "Mark new EPC as sorted"}
                                    icon="done"
                                    onClick={() => props.dispatch({
                                        type: "LEGAL_CASE_EPC_SET_SORTED",
                                        payload: { ...epc.set_sorted, edited: { ...epc.set_sorted.edited, new_epc_sorted: !data.new_epc_sorted } },
                                    })}
                                />
                            </SpacingRow>
                        }
                    </SpacingColumn>
                }

                {/* ACTIONS */}
                <CRMHorizontalSeperatorLine />
                <SpacingRow spacing={CRMSpacing.SMALL}>
                    <CRMButtonQuaternary
                        label="Re-run lookup"
                        icon="repeat"
                        onClick={() => props.dispatch({ type: "LEGAL_CASE_EPC_RERUN_LOOKUP", payload: epc.rerun })}
                    />
                    {status === "pending" &&
                        <CRMButtonQuaternary
                            label="No EPC available"
                            onClick={() => props.dispatch({ type: "LEGAL_CASE_EPC_MARK_NONE_FOUND", payload: epc.mark_not_required })}
                        />
                    }
                    {status !== "not_required" &&
                        <CRMButtonQuaternary
                            label="Mark as not required"
                            onClick={() => props.dispatch({ type: "LEGAL_CASE_EPC_MARK_NOT_REQUIRED", payload: epc.mark_not_required })}
                        />
                    }
                </SpacingRow>

                {/* SEARCH */}
                <CRMHorizontalSeperatorLine />
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <CRMTitleSubSection>Search by postcode</CRMTitleSubSection>
                    <CRMFlatInputGeneral
                        placeholder="Start typing a postcode…"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />

                    {(isSearching || isAttaching) &&
                        <CRMLoadingBar duration={4000} />
                    }

                    {isAttaching &&
                        <CRMParagraph><WeightMedium>Attaching the certificate and saving it to the doc store…</WeightMedium></CRMParagraph>
                    }

                    {needsCertificate && !hasReceivedDocument && results.length > 0 &&
                        <CRMParagraph>
                            <WeightMedium>
                                {`Attach the right certificate below. We couldn't match one automatically — ${results.length === 1 ? "1 is" : `${results.length} are`} registered at ${epc.search.original.search_term.trim()}.`}
                            </WeightMedium>
                        </CRMParagraph>
                    }

                    {results.length > 0 &&
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            {results.map((result) => (
                                <Background
                                    key={result.certificate_number}
                                    color={CRMColors.NEUTRAL_PAPER}
                                    padding={`${CRMSpacing.SMALL} ${CRMSpacing.MEDIUM}`}
                                    borderRadius="5px"
                                    borderDefinition={`1px solid ${CRMColors.NEUTRAL_8}`}
                                >
                                    <SpacingRow spacing={CRMSpacing.SMALL} childSize="1fr auto" alignItems="center">
                                        <SpacingColumn spacing={CRMSpacing.TINY}>
                                            <FontQuicksand>
                                                <Text size={15} lineHeight={1.25}>
                                                    <WeightBold>{result.address}</WeightBold>
                                                </Text>
                                            </FontQuicksand>
                                            <FontRoboto>
                                                <TextColor color={CRMColors.NEUTRAL_2}>
                                                    <Text size={13} lineHeight={1.3}>
                                                        {result.rating ? `Rating ${result.rating}` : "Rating unknown"}
                                                        {result.registration_date && <>{" · "}{result.registration_date}</>}
                                                    </Text>
                                                </TextColor>
                                            </FontRoboto>
                                        </SpacingColumn>
                                        <CRMButtonIcon
                                            variant="tertiary"
                                            icon={isAttaching && attachingCertificateNumber === result.certificate_number ? "pending" : "attach"}
                                            disabled={isAttaching}
                                            title="Attach this certificate"
                                            onClick={() => props.dispatch({
                                                type: "LEGAL_CASE_EPC_ATTACH",
                                                payload: {
                                                    ...epc.attach,
                                                    edited: {
                                                        ...epc.attach.edited,
                                                        certificate_number: result.certificate_number,
                                                        address: result.address,
                                                        postcode: result.postcode,
                                                        rating: result.rating,
                                                        registration_date: result.registration_date,
                                                        uprn: result.uprn,
                                                    },
                                                },
                                            })}
                                        />
                                    </SpacingRow>
                                </Background>
                            ))}
                        </SpacingColumn>
                    }

                    {!isSearching && !isAttaching && hasSearchedCurrentTerm && results.length === 0 &&
                        <CRMParagraph><WeightMedium>No certificates are registered at that postcode.</WeightMedium></CRMParagraph>
                    }
                </SpacingColumn>
            </SpacingColumn>
        </CRMAutosaveIndicatorWrap>
    );
};

export default CRMLegalCaseDetailsEpcContainer;
